import { CAST_AI } from '@cast/constants';
import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  Icons,
  ExternalLink,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { getDocsLink } from '@cast/utils';

type HowItWorksItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const HOW_IT_WORKS_ITEMS: HowItWorksItem[] = [
  {
    icon: <Icons.PencilSimpleLine size={20} />,
    title: `${CAST_AI} patches only the converted fields`,
    description: `Your HPA spec stays intact. Only the target type and value on metrics ${CAST_AI} converts are rewritten — every other field (replicas, behavior, custom metrics, labels, annotations) is left untouched.`,
  },
  {
    icon: <Icons.UserCircle size={20} />,
    title: 'You remain the owner',
    description: `${CAST_AI} does not take ownership of your HPAs. Your CI/CD pipelines and GitOps controllers continue to work normally. ${CAST_AI} recalculates the converted fields whenever your source applies an update.`,
  },
  {
    icon: <Icons.ArrowCounterClockwise size={20} />,
    title: 'Reverts to your original spec automatically',
    description: `If you turn the converter off, the HPA is deleted, or the workload's original request can no longer be resolved, ${CAST_AI} rolls the converted fields back to their pre-conversion values. You always have a path back.`,
  },
  {
    icon: <Icons.Warning size={20} />,
    title: "Skips HPAs it can't safely convert",
    description:
      'Workloads using autoscaling: both, HPAs with external or custom metrics, and HPAs whose owning workload has no resolvable requests are left untouched. You can see which workloads are skipped from the conversion preview.',
  },
];

type ConversionRow = {
  label: string;
  status: string;
  statusColor: string;
};

const CONVERSION_TABLE: ConversionRow[] = [
  {
    label: 'Native HPA — CPU / memory',
    status: '✓ Converted',
    statusColor: tokens.theme.fg.status.success,
  },
  {
    label: 'KEDA — CPU / memory triggers',
    status: '✓ Converted',
    statusColor: tokens.theme.fg.status.success,
  },
  {
    label: 'External / custom metrics',
    status: '— Untouched',
    statusColor: tokens.theme.fg.structural.secondary,
  },
  {
    label: 'Workloads with autoscaling: both',
    status: '— Skipped',
    statusColor: tokens.theme.fg.structural.secondary,
  },
];

export const HpaConverterHowItWorksDrawer = (props: DrawerProps) => {
  return (
    <Drawer testId="hpa-converter-how-it-works-drawer" size="medium" {...props}>
      <DrawerHeader>
        <Stack gap={2}>
          <Typography variant="A2" color={tokens.theme.fg.structural.secondary}>
            HPA CONVERTER
          </Typography>
          <Typography variant="h3">How it works</Typography>
        </Stack>
      </DrawerHeader>
      <DrawerBody>
        <DrawerContent>
          <Stack gap={24}>
            <Typography
              variant="P14R"
              color={tokens.theme.fg.structural.secondary}
            >
              When enabled, {CAST_AI} rewrites percentage-based utilization
              targets on matching HPAs into AverageValue targets calculated from
              each workload&apos;s resolved CPU and memory requests. Here&apos;s
              exactly what that means for your HPAs.
            </Typography>

            <Stack gap={20}>
              {HOW_IT_WORKS_ITEMS.map((item) => (
                <Stack key={item.title} direction="row" gap={12}>
                  <Box
                    flexShrink={0}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      backgroundColor: tokens.theme.bg.status.infoSubtle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: tokens.theme.fg.status.onInfoSubtle,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Stack gap={4}>
                    <Typography variant="P14M">{item.title}</Typography>
                    <Typography
                      variant="P12R"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {item.description}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>

            <Divider />

            <Stack gap={12}>
              <Typography variant="P14M">What gets converted</Typography>
              <Stack gap={0}>
                {CONVERSION_TABLE.map((row, i) => (
                  <Stack
                    key={row.label}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    py={10}
                    sx={{
                      borderBottom:
                        i < CONVERSION_TABLE.length - 1 ? '1px solid' : 'none',
                      borderColor: tokens.theme.border.structural.neutralSubtle,
                    }}
                  >
                    <Typography variant="P12R">{row.label}</Typography>
                    <Typography variant="P12M" color={row.statusColor}>
                      {row.status}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            <ExternalLink link={getDocsLink('docs_woop_hpa')}>
              Read the full documentation
            </ExternalLink>
          </Stack>
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  );
};
